/**
 * Visão geral da mesa (TRoomOverviewBigCard): abre ao clicar num cartão. A capa em destaque leva o
 * sistema, o nome, o status e os selos; embaixo dela, uma faixa com quem está na mesa, o mestre e a
 * criação, e depois como é a mesa (língua, frequência, visual, cobrança), o "Sobre a mesa" e as
 * boas-vindas (em Talemark), as características e a sua última visita.
 * O rodapé fica fixo: a senha, quando a mesa pede, curtir, não curti, copiar o link, falar com o
 * mestre (a conversa direta com o dono, para quem não é ele) e entrar.
 */
import { useEffect, useId, useState } from 'react';
import { useLanguage, useT } from '../i18n/index.js';
import { TalemarkView } from '../room/Talemark.js';
import type { RoomCard, RoomVisuals } from '../shared.js';
import { useApp } from '../store.js';
import { Button, TextEdit } from '../ui/controls.js';
import { Icon, type IconName } from '../ui/icons.js';
import { toast } from '../ui/Toaster.js';
import { languageName, payLabel } from './roomDetails.js';
import { coverOf, roomLink, RoomMark, systemOf } from './RoomTile.js';
import { useExplore } from './state.js';
import { openDm, useCanDm } from '../social/dm.js';

const VISUALS_ICON: Record<RoomVisuals, IconName> = { maps: 'map', 'some-images': 'image', 'theater-of-mind': 'chat' };

export function RoomOverviewPopup({ card: opened, onClose }: { card: RoomCard; onClose: () => void }) {
  const t = useT();
  const language = useLanguage((s) => s.language);
  const me = useApp((s) => s.user);
  const activeRoomId = useApp((s) => s.activeRoomId);
  const joinRoom = useApp((s) => s.joinRoom);
  const vote = useExplore((s) => s.vote);
  const toggleFavorite = useExplore((s) => s.toggleFavorite);
  // o cartão como está agora nas listas (curtir e favoritar daqui mudam ele); quem abriu pode ter um retrato
  const live = useExplore((s) => {
    for (const tab of Object.values(s.tabs)) {
      const room = tab?.rooms.find((r) => r.id === opened.id);
      if (room) return room;
    }
    return null;
  });
  const card = live ?? opened;
  const canDm = useCanDm();
  const [password, setPassword] = useState('');
  const nameId = useId();
  const mine = card.ownerId === me?.id;
  const privileged = mine || card.amIAuxGm;
  const needsPassword = card.hasPassword && !privileged;
  const here = activeRoomId === card.id;
  const system = systemOf(card);
  const date = new Intl.DateTimeFormat(language, { day: 'numeric', month: 'long', year: 'numeric' });
  const monthYear = new Intl.DateTimeFormat(language, { month: 'short', year: 'numeric' });
  const dateTime = new Intl.DateTimeFormat(language, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  const likes = t(card.likes === 1 ? 'explore.overview.likes.one' : 'explore.overview.likes.many', { n: card.likes });
  const dislikes = t(card.dislikes === 1 ? 'explore.overview.dislikes.one' : 'explore.overview.dislikes.many', { n: card.dislikes });
  // como é a mesa: só o que o mestre informou. A cobrança aparece quando há algum outro detalhe ou
  // quando a mesa cobra; sozinha, "Gratuita" diria pouco de uma mesa que não preencheu nada
  const details: { key: string; icon: IconName; label: string }[] = [
    ...(card.language ? [{ key: 'language', icon: 'globe' as const, label: languageName(card.language, language) }] : []),
    ...(card.frequency ? [{ key: 'frequency', icon: 'calendar' as const, label: t(`roomDetails.frequency.${card.frequency}`) }] : []),
    ...(card.visuals ? [{ key: 'visuals', icon: VISUALS_ICON[card.visuals], label: t(`roomDetails.visuals.${card.visuals}`) }] : []),
  ];
  if (details.length > 0 || card.payToPlay) details.push({ key: 'pay', icon: 'coin', label: payLabel(card, t) });

  // Esc fecha (o foco fica no cartão que abriu, fora daqui: o teclado é ouvido na janela, como no Dialog)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  function enter() {
    if (needsPassword && !password && !here) return;
    if (!here) joinRoom(card.id, needsPassword ? password : null);
    else joinRoom(card.id);
    onClose();
  }

  function copyLink() {
    void navigator.clipboard?.writeText(roomLink(card.id)).then(() => toast(t('explore.menu.copied'), 'success'));
  }

  return (
    <div className="rd-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="rd-bigcard" role="dialog" aria-modal="true" aria-labelledby={nameId}>
        <div className="rd-bigcard__scroll">
          <div className="rd-bigcard__cover" style={{ backgroundImage: coverOf(card) }}>
            <span className="rd-bigcard__badges">
              {mine ? <span className="rd-bigcard__badge">{t('explore.card.mine')}</span> : null}
              {!mine && card.amIAuxGm ? <span className="rd-bigcard__badge">{t('explore.card.auxGm')}</span> : null}
              {card.hasPassword ? (
                <span className="rd-bigcard__badge">
                  <Icon name="lock" size={14} />
                  {t('explore.card.password')}
                </span>
              ) : null}
              {card.haveVacancies ? (
                <span className="rd-bigcard__badge">
                  <Icon name="userPlus" size={14} />
                  {t('explore.card.vacancies')}
                </span>
              ) : null}
              {card.restrictedUnder18 ? <span className="rd-bigcard__badge rd-bigcard__badge--warn">{t('explore.card.adult')}</span> : null}
            </span>
            <span className="rd-bigcard__corner">
              <button
                type="button"
                className={`rd-bigcard__round${card.isFavorite ? ' is-on' : ''}`}
                aria-label={t(card.isFavorite ? 'explore.menu.unfavorite' : 'explore.menu.favorite')}
                aria-pressed={card.isFavorite}
                onClick={() => void toggleFavorite(card)}
              >
                <Icon name="star" size={18} strokeWidth={1.8} filled={card.isFavorite} />
              </button>
              <button type="button" className="rd-bigcard__round" aria-label={t('common.close')} onClick={onClose}>
                <Icon name="close" size={18} />
              </button>
            </span>
            <div className="rd-bigcard__head">
              <RoomMark card={card} className="rd-bigcard__mark" />
              <div className="rd-bigcard__title">
                {system ? <p className="rd-bigcard__system">{system}</p> : null}
                <h2 className="rd-bigcard__name" id={nameId}>
                  {card.name}
                </h2>
                {card.statusMessage ? <p className="rd-bigcard__status">{card.statusMessage}</p> : null}
              </div>
            </div>
          </div>

          <dl className="rd-bigcard__stats">
            <div>
              <dt>{t('explore.overview.onlineNow')}</dt>
              <dd className={card.onlineCount ? 'is-live' : undefined}>
                <span className="rd-home__dot" aria-hidden="true" />
                {card.onlineCount}
              </dd>
            </div>
            <div>
              <dt className={card.ownerOnline ? 'is-live' : undefined}>{t(card.ownerOnline ? 'explore.card.gmOnline' : 'explore.card.gmOffline')}</dt>
              <dd>{card.ownerName || '—'}</dd>
            </div>
            <div>
              <dt>{t('explore.overview.created')}</dt>
              <dd title={date.format(card.createdAt)}>{monthYear.format(card.createdAt)}</dd>
            </div>
          </dl>

          <div className="rd-bigcard__body">
            {details.length ? (
              <ul className="rd-bigcard__details" aria-label={t('explore.overview.details')}>
                {details.map((detail) => (
                  <li key={detail.key} className={detail.key === 'pay' && card.payToPlay ? 'is-paid' : undefined}>
                    <Icon name={detail.icon} size={16} strokeWidth={1.8} />
                    {detail.label}
                  </li>
                ))}
              </ul>
            ) : null}
            {card.about ? (
              <section className="rd-bigcard__desc">
                <h3 className="rd-bigcard__h">{t('explore.overview.about')}</h3>
                <TalemarkView text={card.about} />
              </section>
            ) : null}
            {card.welcomeMessage ? (
              // com o "Sobre a mesa" em cima, as boas-vindas ganham o título delas
              <section className="rd-bigcard__desc" aria-label={card.about ? undefined : t('explore.overview.description')}>
                {card.about ? <h3 className="rd-bigcard__h">{t('create.welcome')}</h3> : null}
                <TalemarkView text={card.welcomeMessage} />
              </section>
            ) : null}
            {card.tags.length ? (
              <div className="rd-bigcard__tags">
                {card.tags.map((tag) => (
                  <span key={tag} className="rd-bigcard__tag">
                    {t(`roomTag.${tag}`) === `roomTag.${tag}` ? tag : t(`roomTag.${tag}`)}
                  </span>
                ))}
              </div>
            ) : null}
            <p className="rd-bigcard__visit">
              <Icon name="clock" size={16} strokeWidth={1.8} />
              {card.lastAccessAt ? t('explore.overview.visited', { when: dateTime.format(card.lastAccessAt) }) : t('explore.overview.notVisited')}
            </p>
          </div>
        </div>

        <form
          className="rd-bigcard__join"
          onSubmit={(e) => {
            e.preventDefault();
            enter();
          }}
        >
          {needsPassword && !here ? (
            <TextEdit look="filled" icon="lock" type="password" value={password} onChange={setPassword} placeholder={t('explore.overview.password')} aria-label={t('explore.overview.password')} autoFocusOnMount />
          ) : null}
          <div className="rd-bigcard__actions">
            <button
              type="button"
              className={`rd-bigcard__act${card.myVote === 1 ? ' is-on' : ''}`}
              aria-label={`${t(card.myVote === 1 ? 'explore.menu.unlike' : 'explore.menu.like')} · ${likes}`}
              aria-pressed={card.myVote === 1}
              onClick={() => void vote(card, 1)}
            >
              <Icon name="thumbUp" size={18} strokeWidth={1.8} />
              {card.likes}
            </button>
            <button
              type="button"
              className={`rd-bigcard__act${card.myVote === -1 ? ' is-on' : ''}`}
              aria-label={`${t(card.myVote === -1 ? 'explore.menu.undislike' : 'explore.menu.dislike')} · ${dislikes}`}
              aria-pressed={card.myVote === -1}
              onClick={() => void vote(card, -1)}
            >
              <Icon name="thumbDown" size={18} strokeWidth={1.8} />
              {card.dislikes}
            </button>
            <button type="button" className="rd-bigcard__act" aria-label={t('explore.menu.copyLink')} onClick={copyLink}>
              <Icon name="link" size={18} strokeWidth={1.8} />
            </button>
            {canDm && !mine ? (
              <button
                type="button"
                className="rd-bigcard__act"
                title={t('room.talkToGm')}
                onClick={() => {
                  // a conversa abre embaixo, e a visão geral (que fica por cima) sai da frente
                  onClose();
                  openDm(card.ownerId);
                }}
              >
                <Icon name="mail" size={18} strokeWidth={1.8} />
                <span className="rd-bigcard__actText">{t('room.talkToGm')}</span>
              </button>
            ) : null}
            <Button type="submit" variant="action" className="rd-bigcard__go" disabled={needsPassword && !here && !password}>
              {here ? t('explore.overview.rejoin') : t('explore.overview.join')}
              <Icon name="arrowRight" size={18} />
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
